import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown, X } from 'lucide-react'
import styles from './SearchableSelect.module.scss'

export interface SearchableSelectOption {
  value: string | number
  label: string
}

interface SearchableSelectProps {
  value: string | number
  onChange: (value: string) => void
  options: SearchableSelectOption[]
  placeholder?: string
  disabled?: boolean
  className?: never
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value, onChange, options, placeholder = 'Select…', disabled = false,
}) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(search.toLowerCase())
  )

  const selected = options.find((opt) => String(opt.value) === String(value))

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (optValue: string | number) => {
    onChange(String(optValue))
    setOpen(false)
    setSearch('')
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('')
    setSearch('')
  }

  const handleToggle = () => {
    if (disabled) return
    setOpen((prev) => !prev)
    if (!open) {
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={[styles.trigger, !selected ? styles.placeholder : ''].filter(Boolean).join(' ')}
      >
        <span className={styles.valueText}>
          {selected ? selected.label : placeholder}
        </span>
        <span className={styles.controls}>
          {selected && (
            <span role="button" tabIndex={-1} onClick={handleClear} onKeyDown={() => {}} className={styles.clearBtn}>
              <X style={{ width: 14, height: 14 }} />
            </span>
          )}
          <ChevronDown className={[styles.chevron, open ? styles.chevronOpen : ''].filter(Boolean).join(' ')} />
        </span>
      </button>

      {open && (
        <div className={styles.dropdown}>
          <div className={styles.searchWrap}>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className={styles.searchInput}
            />
          </div>
          <ul className={styles.optionsList}>
            {filtered.length === 0 ? (
              <li className={styles.noResults}>No results found</li>
            ) : (
              filtered.map((opt) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    className={[styles.option, String(opt.value) === String(value) ? styles.optionActive : ''].filter(Boolean).join(' ')}
                  >
                    {opt.label}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  )
}
